import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Link2, Calculator, Clock, Save, RefreshCw, Mail, Send, Palette, ExternalLink, Puzzle, Lock, FileText, ShieldCheck } from "lucide-react";
import { StoresRoutingCard } from "@/components/StoresRoutingCard";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { ADDON_STATUS } from "@/lib/packages";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [dealer, setDealer] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [carfaxCode, setCarfaxCode] = useState("");
  const [carfaxBusy, setCarfaxBusy] = useState(false);

  const saveCarfax = async (override) => {
    setCarfaxBusy(true);
    try {
      const { data } = await api.post("/dealer/carfax/partner", { code: typeof override === "string" ? override : carfaxCode });
      setDealer(data.dealer);
      setForm((f) => ({ ...f, ...data.dealer }));
      setCarfaxCode(data.dealer.carfax_partner_code || "");
      (data.recognized || data.cleared ? toast.success : toast.warning)(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCarfaxBusy(false);
    }
  };

  useEffect(() => {
    api
      .get("/dealer")
      .then(({ data }) => {
        setDealer(data);
        setForm(data);
        setCarfaxCode(data.carfax_partner_code || "");
      })
      .catch(() => toast.error("Failed to load settings"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/dealer", {
        name: form.name,
        website_url: form.website_url,
        inventory_source_url: form.inventory_source_url,
        payment_calculator_url: form.payment_calculator_url,
        crm_email: form.crm_email,
        accent_color: form.accent_color,
        logo_url: form.logo_url,
        phone: form.phone,
        address: form.address,
        city: form.city || "",
        state: (form.state || "").toUpperCase(),
        zip: form.zip || "",
        sync_frequency: form.sync_frequency,
        max_vehicles: form.max_vehicles === "" || form.max_vehicles == null ? 0 : Number(form.max_vehicles) || 0,
        wall_lower_price: !!form.wall_lower_price,
        lower_price_type: form.lower_price_type || "amount",
        lower_price_value: Number(form.lower_price_value) || 0,
        wall_carfax: !!form.wall_carfax,
        wall_payment: !!form.wall_payment,
        wall_estimate: !!form.wall_estimate,
        wall_recon: !!form.wall_recon,
        wall_time_on_lot: !!form.wall_time_on_lot,
        wall_acquisition: !!form.wall_acquisition,
      });
      setDealer(data);
      setForm(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/dealer/crm/test");
      toast.success(`Test ADF lead sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!dealer) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" />
      </div>
    );
  }

  const field = (key, label, placeholder, testid) => (
    <div>
      <Label className="text-slate-300 mb-1.5 block">{label}</Label>
      <Input
        data-testid={testid}
        disabled={!isAdmin}
        value={form[key] || ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 disabled:opacity-60"
      />
    </div>
  );

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Settings</h1>
        <p className="text-sm text-slate-400 mt-1">
          Connect your inventory source and control the auto-sync cadence.
        </p>
      </div>

      <div className="space-y-6">
        <section className="tracing-top rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Inventory connection</h2>
          </div>
          <div className="space-y-4">
            {field("inventory_source_url", "Dealership inventory URL", "https://yourdealership.com", "inventory-sync-url-input")}
            <p className="text-xs text-slate-500 -mt-2">
              We read this site's sitemap and pull each vehicle automatically. Sold cars are removed on the next sync.
            </p>
            {field("payment_calculator_url", "Payment calculator URL (optional)", "https://yourdealership.com/finance", "payment-calculator-url-input")}
            <p className="text-xs text-slate-500 -mt-2">
              Leave blank to send shoppers to each vehicle's own page on your site.
            </p>
          </div>
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="crm-settings-section">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">CRM lead delivery (ADF XML)</h2>
          </div>
          <div className="space-y-4">
            {field("crm_email", "CRM lead inbox", "leads@yourdealership.com", "crm-email-input")}
            <p className="text-xs text-slate-500 -mt-2">
              Every shopper contact request is emailed here instantly as an ADF 1.0 XML lead — vehicle, customer,
              quiz answers and the AI-matched specialist included. With stores set up below, this is the <b className="text-slate-300">group default</b> inbox.
            </p>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  onClick={sendTest}
                  disabled={testing || !dealer.crm_email}
                  data-testid="send-test-adf-btn"
                  className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                >
                  {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Send test lead to CRM
                </Button>
                {!dealer.crm_email && (
                  <span className="text-xs text-amber-400">Save a CRM inbox first to enable the test send.</span>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Auto-sync</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-1.5 block">Refresh rate</Label>
              <Select
                value={form.sync_frequency}
                onValueChange={(v) => setForm({ ...form, sync_frequency: v })}
                disabled={!isAdmin}
              >
                <SelectTrigger
                  data-testid="sync-frequency-select"
                  className="bg-slate-950 border-slate-800 text-slate-200"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                  <SelectItem value="manual">Manual only</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 mb-1.5 block">Max vehicles per sync</Label>
              <Input
                type="number"
                min={0}
                data-testid="max-vehicles-input"
                disabled={!isAdmin}
                value={form.max_vehicles ?? ""}
                onChange={(e) => setForm({ ...form, max_vehicles: e.target.value })}
                placeholder="0 = whole lot"
                className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 disabled:opacity-60"
              />
              <p className="text-[11px] text-slate-500 mt-1">0 syncs your entire inventory (up to 1,000 vehicles).</p>
            </div>
          </div>
          {dealer.last_synced_at && (
            <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
              <RefreshCw className="h-3 w-3" /> Last synced {new Date(dealer.last_synced_at).toLocaleString()} ·{" "}
              {dealer.vehicle_count} active vehicles
            </p>
          )}
        </section>

        <StoresRoutingCard dealer={dealer} isAdmin={isAdmin} onSaved={(d) => { setDealer(d); setForm((f) => ({ ...f, ...d })); }} />

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="addons-section">
          <div className="flex items-center gap-2 mb-4">
            <Puzzle className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Package & add-ons</h2>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <div className="text-sm font-medium">Basic</div>
                <div className="text-[11px] text-slate-500">QR stickers, landing pages, CARFAX & payment links, CRM leads</div>
              </div>
              <Badge variant="outline" className={ADDON_STATUS.active.cls}>Included</Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <div className="text-sm font-medium">Specialist Match <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech ml-1">add-on</span></div>
                <div className="text-[11px] text-slate-500">Style assessment, specialist profiles, CRM scoring, matched leads</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" data-testid="addon-status-badge" className={(ADDON_STATUS[dealer.addon_specialist_match] || ADDON_STATUS.none).cls}>
                  {(ADDON_STATUS[dealer.addon_specialist_match] || ADDON_STATUS.none).label}
                </Badge>
                {dealer.addon_specialist_match !== "active" && (
                  <Link to="/dashboard/specialists" data-testid="addon-manage-link" className="text-xs text-[#7FC3FF] hover:text-white underline underline-offset-2">
                    {dealer.addon_specialist_match === "requested" ? "View" : "Request"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="carfax-section">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-4 w-4 text-[#3777BC]" />
            <h2 className="font-heading font-bold">CARFAX reports</h2>
            {dealer.carfax_partner_verified ? (
              <Badge data-testid="carfax-status-badge" className="ml-auto bg-emerald-950/60 text-emerald-300 border border-emerald-800/60">Live · code {dealer.carfax_partner_code}</Badge>
            ) : (
              <Badge data-testid="carfax-status-badge" variant="outline" className="ml-auto border-amber-800/60 bg-amber-950/40 text-amber-300">Not live — shoppers can request the report</Badge>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Free CARFAX reports on your QR pages need your CARFAX dealer subscription. If your website already links a report on each pre-owned unit, Vinconex picks those up on every sync. Otherwise enter the <b className="text-slate-300">partner code</b> from your CARFAX rep — we check it with CARFAX before turning reports on. Until then, pre-owned pages show <i>Request CARFAX Report</i>, which captures the shopper as a lead so you can send it.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="carfax-partner-input"
              value={carfaxCode}
              onChange={(e) => setCarfaxCode(e.target.value)}
              placeholder="CARFAX partner code (e.g. ABC_1)"
              disabled={!isAdmin}
              className="bg-slate-950 border-slate-800 text-slate-200 sm:max-w-xs font-mono-tech"
            />
            <Button onClick={saveCarfax} disabled={!isAdmin || carfaxBusy} data-testid="carfax-verify-btn" className="bg-[#3777BC] hover:bg-[#3777BC]/85 font-bold">
              {carfaxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="mr-2 h-4 w-4" /> Verify with CARFAX &amp; save</>}
            </Button>
            {dealer.carfax_partner_code && (
              <Button variant="ghost" onClick={() => saveCarfax("")} disabled={!isAdmin || carfaxBusy} data-testid="carfax-clear-btn" className="text-slate-400 hover:text-white">Remove code</Button>
            )}
          </div>
          {dealer.carfax_partner_code && !dealer.carfax_partner_verified && (
            <p className="text-xs text-amber-300/90 mt-3" data-testid="carfax-unverified-note">
              CARFAX doesn't recognise <span className="font-mono-tech">{dealer.carfax_partner_code}</span>{dealer.carfax_partner_source === "site" ? " (found on your website)" : ""} — reports stay off so shoppers never hit a blank CARFAX page.
            </p>
          )}
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="lead-walls-section">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Lead walls</h2>
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Ask shoppers for their contact info before they see a lower price, the CARFAX report or your payment calculator. Each unlock sends an ADF lead to your CRM. One unlock opens all three for an hour.
          </p>
          <div className="space-y-2">
            <div className="rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">Lower price</div>
                  <div className="text-[11px] text-slate-500">List price stays visible; shoppers unlock the reduced price you set below.</div>
                </div>
                <Switch data-testid="wall-lower-price-switch" disabled={!isAdmin} checked={!!form.wall_lower_price} onCheckedChange={(v) => setForm({ ...form, wall_lower_price: v })} />
              </div>
              {form.wall_lower_price && (
                <div className="mt-3 grid grid-cols-5 gap-3 items-end" data-testid="lower-price-config">
                  <div className="col-span-2">
                    <Label className="text-slate-300 mb-1.5 block">Reduction type</Label>
                    <Select value={form.lower_price_type || "amount"} onValueChange={(v) => setForm({ ...form, lower_price_type: v })} disabled={!isAdmin}>
                      <SelectTrigger data-testid="lower-price-type-select" className="bg-slate-900 border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                        <SelectItem value="amount">$ off list price</SelectItem>
                        <SelectItem value="percent">% off list price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-slate-300 mb-1.5 block">{form.lower_price_type === "percent" ? "Percent off" : "Dollars off"}</Label>
                    <div className="relative">
                      {form.lower_price_type !== "percent" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>}
                      <Input
                        type="number" min="0" step={form.lower_price_type === "percent" ? "0.5" : "50"}
                        data-testid="lower-price-value-input" disabled={!isAdmin}
                        value={form.lower_price_value ?? ""} onChange={(e) => setForm({ ...form, lower_price_value: e.target.value })}
                        placeholder={form.lower_price_type === "percent" ? "3" : "500"}
                        className={`bg-slate-900 border-slate-800 focus:border-[#0A8CF2] text-slate-100 ${form.lower_price_type !== "percent" ? "pl-7" : ""}`}
                      />
                      {form.lower_price_type === "percent" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>}
                    </div>
                  </div>
                  {!(Number(form.lower_price_value) > 0) && (
                    <p className="col-span-5 text-[11px] text-amber-400" data-testid="lower-price-warning">Set a reduction above 0 — until then the wall stays hidden on your pages.</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <div className="text-sm font-medium">CARFAX report</div>
                <div className="text-[11px] text-slate-500">Pre-owned units with a live report — it opens after the shopper leaves their details. Units without one show "Request CARFAX Report" either way.</div>
              </div>
              <Switch data-testid="wall-carfax-switch" disabled={!isAdmin} checked={!!form.wall_carfax} onCheckedChange={(v) => setForm({ ...form, wall_carfax: v })} />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <div className="text-sm font-medium">Payment calculator</div>
                <div className="text-[11px] text-slate-500">Uses your calculator URL below, or the vehicle's listing page when none is set.</div>
              </div>
              <Switch data-testid="wall-payment-switch" disabled={!isAdmin} checked={!!form.wall_payment} onCheckedChange={(v) => setForm({ ...form, wall_payment: v })} />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <div className="text-sm font-medium">Repair estimate (As-Is units)</div>
                <div className="text-[11px] text-slate-500">The shop estimate you upload or type in Inventory → As-Is opens after the shopper leaves their details.</div>
              </div>
              <Switch data-testid="wall-estimate-switch" disabled={!isAdmin} checked={!!form.wall_estimate} onCheckedChange={(v) => setForm({ ...form, wall_estimate: v })} />
            </div>
            <div className="pt-3 mt-1 border-t border-slate-800" data-testid="insider-walls">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech mb-2">Insider details · shown only on units where you enter them (Inventory → clipboard button)</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">Reconditioning report</div>
                    <div className="text-[11px] text-slate-500">Requested or completed — plus the shop's report (PDF/photo) when you upload it.</div>
                  </div>
                  <Switch data-testid="wall-recon-switch" disabled={!isAdmin} checked={!!form.wall_recon} onCheckedChange={(v) => setForm({ ...form, wall_recon: v })} />
                </div>
                <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">Time on lot</div>
                    <div className="text-[11px] text-slate-500">Days since the unit hit the lot, from your website's lot date. Hide it per unit in Inventory.</div>
                  </div>
                  <Switch data-testid="wall-time-on-lot-switch" disabled={!isAdmin} checked={!!form.wall_time_on_lot} onCheckedChange={(v) => setForm({ ...form, wall_time_on_lot: v })} />
                </div>
                <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
                  <div>
                    <div className="text-sm font-medium">How we acquired it</div>
                    <div className="text-[11px] text-slate-500">Trade-in, lease return, auction, dealer purchase, rental/fleet — with your shopper-facing note.</div>
                  </div>
                  <Switch data-testid="wall-acquisition-switch" disabled={!isAdmin} checked={!!form.wall_acquisition} onCheckedChange={(v) => setForm({ ...form, wall_acquisition: v })} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Dealership details</h2>
          </div>
          <div className="space-y-4">
            {field("name", "Dealership name", "Abeloff Nissan", "dealer-name-input")}
            {field("website_url", "Website", "https://abeloffnissan.com")}
            <div className="grid sm:grid-cols-2 gap-4">
              {field("phone", "Phone", "(272) 271-2725")}
              {field("address", "Street address", "3259 Route 611")}
            </div>
            <div className="grid grid-cols-6 gap-4">
              <div className="col-span-3">{field("city", "City", "Bartonsville", "dealer-city-input")}</div>
              <div className="col-span-1">{field("state", "State", "PA", "dealer-state-input")}</div>
              <div className="col-span-2">{field("zip", "ZIP", "18321", "dealer-zip-input")}</div>
            </div>
            <p className="text-[11px] text-slate-500">City, state and ZIP let Vinconex staff find your rooftop in the admin console.</p>
          </div>
        </section>

        <section className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-6" data-testid="branding-section">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="h-4 w-4 text-[#0A8CF2]" />
            <h2 className="font-heading font-bold">Branding</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-1.5 block">Accent color</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="accent-color-input"
                  disabled={!isAdmin}
                  value={/^#[0-9a-f]{6}$/i.test(form.accent_color || "") ? form.accent_color : "#0A8CF2"}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="h-10 w-12 rounded border border-slate-800 bg-slate-950 cursor-pointer disabled:opacity-60"
                />
                <Input
                  data-testid="accent-color-hex-input"
                  disabled={!isAdmin}
                  value={form.accent_color || ""}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 font-mono-tech uppercase disabled:opacity-60"
                />
              </div>
            </div>
            {field("logo_url", "Logo URL", "https://…/logo.png", "logo-url-input")}
          </div>
          <div className="mt-4 rounded-xl border border-[#2A2E3D] bg-[#0A0B0E] p-5" style={brandStyle(form.accent_color)} data-testid="branding-preview">
            <div className="flex items-center justify-between">
              <BrandLogo dealer={{ name: form.name, logo_url: form.logo_url }} />
              <span className="text-xs text-slate-500 font-mono-tech">shopper preview</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="px-4 py-2 rounded-md bg-brand text-white text-sm font-bold">Match me with a specialist</span>
              <span className="px-4 py-2 rounded-md border border-brand/40 bg-brand/10 text-brand-soft text-sm">See new arrivals</span>
            </div>
          </div>
          {dealer.slug && (
            <p className="text-xs text-slate-500 mt-4 flex items-center gap-1.5">
              Public page:{" "}
              <a href={`/d/${dealer.slug}`} target="_blank" rel="noreferrer" data-testid="public-page-link" className="text-[#7FC3FF] hover:text-white inline-flex items-center gap-1">
                /d/{dealer.slug} <ExternalLink className="h-3 w-3" />
              </a>
              <span className="ml-2 uppercase font-mono-tech">status: {dealer.status}</span>
            </p>
          )}
        </section>

        {isAdmin ? (
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-settings-btn"
            className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shadow-lg shadow-blue-900/40"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save settings
          </Button>
        ) : (
          <p className="text-xs text-slate-500">Only admins can edit these settings.</p>
        )}
      </div>
    </div>
  );
}
